/* eslint-disable react/jsx-indent, no-restricted-properties */
import React, {
  useState, useEffect, useContext, useCallback,
} from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import DateTimePicker from 'react-datetime-picker';
import TimePicker from 'rc-time-picker';
import 'rc-time-picker/assets/index.css';
import styled, { createGlobalStyle } from 'styled-components';
import { useLazyQuery, useMutation } from '@apollo/react-hooks';
import moment from 'moment-timezone';
import ReactTooltip from 'react-tooltip';
import toast from 'react-hot-toast';
import Compress from 'compress.js';
import Select from 'react-select';
import { getDateByOffset } from '../../util/Time';
import Input, { TextAreaInput } from '../Input';
import Button from '../Button';
import BaseModal, { ModalList } from '../modals/BaseModal';
import { IconEnum } from '../Icons';
import Icon, { Icons } from '../Icon';
import { getErrorMessage } from '../../util/ErrorUtil';
import ReCropper from '../../util/ReCropper';
import {
  TriggerEvents,
  CampaignModal,
  mapTriggerEventOptions,
} from '../../util/Campaign';
import { getPlivoSegmentCount, isGsm7, countMessageChars } from '../../util/PlivoSegmentCalculation';
import {
  ButtonContainer,
  Form,
  MessageDetails,
  ImageDataContainer,
  ImageInputContainer,
  InputSection,
  Label,
  RadioContainer,
  MessageDiv,
} from './CampaignFormElements';
import {
  CREATE_CAMPAIGN,
  DELETE_CAMPAIGN,
  MOST_RECENT_CAMPAIGN,
  TEST_CAMPAIGN,
  UPDATE_CAMPAIGN,
  UPLOAD_ORG_FILES,
  GET_CONTACT_BY_TAGS,
} from '../../graphql/queries';
import colors from '../../styles/colors';
import { useMobileMedia } from '../../util/MediaQuery';
import { AppContext } from '../../context/AppContext';
import { UserContext } from '../../context/UserContext';

const TooltipStyles = createGlobalStyle`
.__react_component_tooltip{
      display: inline-block !important;
      max-width: 300px;
    }

    .css-1rhbuit-multiValue{
      font-size:13px !important;
      background: rgba(26,188,156,0.1) !important;
      &>div{
        color: ${colors.seaGreen};
      };
    };

`;

const DatePickerStyles = createGlobalStyle`
  
  .react-datetime-picker__inputGroup__leadingZero{
  display: inline-flex;
  align-items: center;
  color:${colors.grey3};
 }

 .react-calendar {
  color:black;
  font-weight:500;
  font-size: 11px !important;
  font-family:'Poppins', sans-serif !important;
 }

 span.rc-time-picker{
   margin-left: 7px;
  border: thin solid grey;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius:8px;
  width: 54px;
  
 }

 .rc-time-picker-input{
  border-radius:8px;
width: 60px;
border: none;
padding: 4px 0;
color:${colors.grey3}; 
font-family: 'Source Sans Pro',sans-serif !important;
 }

 .react-datetime-picker__button{
  padding: 0 6px;
 }

  .react-datetime-picker__inputGroup__input {
    color: ${(props) => (props.active ? colors.seaGreen : colors.grey3)};
  }
  .react-datetime-picker__wrapper{
    border-radius:8px;
    font-size: 13px;
    @media screen and (max-width:450px){
      width: max-content;
      display: flex;

    }
  }
  .react-datetime-picker__inputGroup__divider{
    color:${colors.grey3};
  }
  .react-datetime-picker__button svg {
   stroke: grey  ;
  }
  .react-datetime-picker__clear-button{
   display:none 
  }
  .react-datetime-picker__inputGroup{
    padding:0px 5px;
  }

  .react-calendar button{
    height:35px;
  }

`;

const StyledTimePicker = styled(TimePicker)`
  & .rc-time-picker-panel-select-option-selected {
    background-color: #edeffe;
    font-weight: normal;
  }

  & .rc-time-picker-clear,
  & .rc-time-picker-clear-icon:after {
    font-size: 13px !important;
  }

  & .rc-time-picker-panel-select,
  & .rc-time-picker-input,
  & .rc-time-picker-panel-input {
    font-family: 'Poppins', sans-serif;
    font-size: 13px !important;
    cursor: pointer;

    ::-webkit-scrollbar {
      width: 0;
      height: 0;
    }
  }
`;

const RemoveIconDiv = styled.div`
  display: block;
  border-radius: 50%;
  position: absolute;
  top: 10px;
  background-color: ${colors.grey};
  right: 10px;
  color: ${colors.white};
  z-index: 500;
  cursor: pointer;
`;

const DisabledImagePlaceholder = styled.div`
  display: flex;
  min-height: 15rem;
  justify-content: center;
  border-radius: 7px;
  align-items: center;
  font-size: 1.2rem;
  color: ${colors.grey3};
  padding: 0 5px;
  border: 1px solid ${colors.grey5};
`;

const CheckBoxSection = styled(InputSection)`
  @media screen and (max-width: 991px) {
    width: 100% !important;
    justify-content: ${(props) => props.justify || 'flex-start'};
    padding: 10px 0 0 0;
  }
  @media screen and (max-width: 480px) {
    flex-direction: column;
  }
`;

const Text = styled.div`
  padding: 10px;
  font-size: 13px;
  color: black;
  white-space: pre-line;
`;
const FileInput = styled.input`
  opacity: 0;
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  cursor: pointer;
`;

const MessageDetailsContainer = styled.div`
  display: flex;
  width: 100%;
  flex-direction: row;
  justify-content: space-between;
  flex-wrap: wrap;
`;

const sendMode = Object.freeze({
  SendNow: 'Send Now',
  Schedule: 'Schedule',
  Draft: 'Draft',
});

export const campaignStatus = Object.freeze({
  Draft: 'Draft',
  Scheduled: 'Scheduled',
  Sent: 'Sent',
});

const formTypes = Object.freeze({
  Add: 'add',
  Edit: 'edit',
  Replicate: 'replicate',
});

const executionStatus = Object.freeze({
  Pending: 'Pending',
  Processing: 'Processing',
  Completed: 'Completed',
  Failed: 'Failed',
});

const CREDITS_PER_MMS = 3;
// Canonical delimiter (must match API): same as used for segment calculation and send body (FR-1).
const CANONICAL_DELIMITER = '\n\n';

function composeCanonicalMessage(text, unsubscribeMessage, includeReferral, referralText) {
  const t = text || '';
  const u = unsubscribeMessage || '';
  if (includeReferral && referralText != null && String(referralText).trim() !== '') {
    return `${t}${CANONICAL_DELIMITER}${referralText}${CANONICAL_DELIMITER}${u}`;
  }
  return `${t}${CANONICAL_DELIMITER}${u}`;
}
// function bytesToSize(bytes) {
//   const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
//   if (bytes === 0) return '0 Byte';
//   const i = parseInt(Math.floor(Math.log(bytes) / Math.log(1024)));
//   return `${Math.round(bytes / Math.pow(1024, i), 2)} ${sizes[i]}`;
// }
// const MAX_SIZE_IN_MB = 0.75;

const ALLOWED_FILE_TYPES = ['.jpeg', '.png', '.gif', '.jpg'];

const FILE_UPLOADING_INDICATOR = 'File is uploading please wait...';

const COMPRESS_OPTIONS = {
  size: 0.495, // the max size in MB, defaults to 2MB
  quality: 0.75, // the quality of the image, max is 1,
  maxWidth: 1080, // the max width of the output image, defaults to 1920px
  maxHeight: 1080, // the max height of the output image, defaults to 1920px
  resize: true, // defaults to true, set false if you do not want to resize the image width and height
  rotate: false, // See the rotation section below
};

const CampaignForm = ({
  values,
  setValues,
  formType = formTypes.Add,
  mmsEnabled,
  shortUrlDomain,
}) => {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const type = searchParams.get('type');

  const { app } = useContext(AppContext);
  const [scheduleDate, setScheduleDate] = useState(
    getDateByOffset(app.orgTimeZoneOffset),
  );
  const [timestamp, setTimeStamp] = useState('');
  const [files] = useState([]);
  const [status, setStatus] = useState(campaignStatus.Draft);
  const [selectTemplateVisible, setSelectTemplateVisible] = useState(false);
  const [sendNow, setSendNow] = useState(true);
  const [credits, setCredits] = useState('1');
  const [tagsContactsCount, setTagsContactsCount] = useState(0);
  const [sendUpdateButtonText, setSendUpdateButtonText] = useState('SEND NOW');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [formError, setFormError] = useState('');
  const [buttonLoading, setButtonLoading] = useState(null);
  const [spamWarningModal, setSpamWarningModal] = useState(false);
  const [optimalCampaignModal, setOptimalCampaignModal] = useState(false);
  const isMobile = useMobileMedia();
  // Replicate mounts only after CreateCampaign has seeded values — initialize local UI state once from those values.
  const isReplicate = formType === formTypes.Replicate;
  const [includeReferralText, setincludeReferralText] = useState(() => (
    isReplicate ? !!values.referralOptIn : false
  ));
  const [shortenUrl, setShortenUrl] = useState(() => (
    isReplicate ? values.isUrlShortningEnabled !== false : true
  ));
  const [customNumbers, setCustomNumbers] = useState([]);
  const [previewCampaignModal, setPreviewCampaignModal] = useState(false);
  const [croppedImage, setCroppedImage] = useState('');
  const [img, setImg] = useState('');
  const [imgName, setImgName] = useState('');
  const [compressing, setCompressing] = useState(false);
  const [recentCampaignData, setRecentCampaignData] = useState(null);
  // Initial character count from canonical message composition (FR-4, FR-5).
  const composeInitialFullMessage = () => composeCanonicalMessage(
    values.text,
    values.unsubscribeMessage,
    includeReferralText,
    values.referralOptInText,
  );
  const [characterCount, setCharacterCount] = useState(countMessageChars(composeInitialFullMessage()));
  const [enteredCharacterCount, setEnteredCharacterCount] = useState(() => countMessageChars(composeInitialFullMessage())); // When shortenUrl: count with full URLs (FR-7).
  const [containsUnicode, setContainsUnicode] = useState(false);
  const [segments, setSegments] = useState(1);
  const [calculatedSegments, setCalculatedSegments] = useState(1); // Store actual calculated segments for display logic
  const [testResponse, setTestResponse] = useState({
    success: true,
    message: '',
  });
  const { user } = useContext(UserContext);
  const [tagsOptions, setTagsOptions] = useState([]);
  const [dropdownOptions, setDropdownOptions] = useState([]);
  const [selectedTags, setSelectedTags] = useState(() => (
    isReplicate && Array.isArray(values.tags)
      ? values.tags.map((tag) => ({ label: tag, value: tag }))
      : []
  ));
  const [selectedTriggerDropdown, setSelectedTriggerDropdown] = useState(() => {
    if (isReplicate && values.isTriggerCampaign) {
      const opts = mapTriggerEventOptions(values.events);
      return opts[0] || TriggerEvents[0];
    }
    return TriggerEvents[0];
  });
  const [getContactsCountByTags, { data, loading: contactsCountLoading }] = useLazyQuery(GET_CONTACT_BY_TAGS, {
    onCompleted(data) {
      setTagsContactsCount(data.getContactByTags);
    },
    fetchPolicy: 'network-only',
  });

  const mapSelectedTags = (selectedTags = []) => {
    return selectedTags?.map((tag) => tag.value);
  };
  const mapSelectedTrigger = (selectedTrigger = []) => {
    return selectedTrigger?.map((trigger) => trigger?.value);
  };
  const getSelectedTriggerEvents = () => {
    if (Array.isArray(selectedTriggerDropdown)) {
      return mapSelectedTrigger(selectedTriggerDropdown);
    }
    if (selectedTriggerDropdown) {
      return mapSelectedTrigger([selectedTriggerDropdown]);
    }
    return [];
  };
  const handleChange = (selectedOption) => {
    setSelectedTags(selectedOption);
  };
  useEffect(() => {
    if (!selectedTriggerDropdown) {
      setSelectedTriggerDropdown(TriggerEvents[0]);
    }
  }, [selectedTriggerDropdown]);

  const handleTriggerChange = (selectedOption) => {
    setSelectedTriggerDropdown(selectedOption);
  };

  useEffect(() => {
    getContactsCountByTags({
      variables: {
        tags: mapSelectedTags(selectedTags) || [],
        isTriggerCampaign: values?.isTriggerCampaign || (type === CampaignModal.TriggerCampaign),
      },
    });
  }, [selectedTags]);

  useEffect(() => {
    setTimeout(() => {
      if (!sendNow) {
        const datePicker = document.getElementsByName('datetime')[0];
        if (datePicker) {
          datePicker.style.opacity = '0';
          datePicker.style.visibility = 'unset';
        }
      }
    }, 0);
  }, [sendNow]);

  useEffect(() => {
    if (user?.organization?.tags) {
      const tagsData = user?.organization?.tags;
      const formattedTags = tagsData.map((tag) => ({ value: tag, label: tag }));
      setTagsOptions(formattedTags);
    }
  }, [user, data]);

  const getConvertedTs = useCallback(
    (date) => {
      const format = 'YY-MM-DD h:mm:s a Z';
      const convertedDate = moment(date)
        .tz(app.orgTimeZone, true)
        .format(format);
      const unixTimeStamp = moment(convertedDate, format).format('X');
      return unixTimeStamp;
    },
    [app.orgTimeZone],
  );

  let submitType = '';

  const [uploadFiles, { loading: uploading }] = useMutation(UPLOAD_ORG_FILES, {
    onCompleted(uplodeData) {
      if (img) {
        setValues((values) => ({
          ...values,
          imageUrl: uplodeData.uploadOrgFiles[0].url,
        }));
      }
    },
    onError(error) {
      console.log(error);
    },
  });

  const [createCampaign, { loading }] = useMutation(CREATE_CAMPAIGN, {
    variables: {
      name: values.name,
      text: values.text,
      unsubscribeMessage: values.unsubscribeMessage,
      status, // back-end asked to send status key
      ...(values.imageUrl && { imageUrl: values.imageUrl }),
      referralOptIn: includeReferralText,
      referralOptInText: includeReferralText ? values.referralOptInText : '',
      type: values.imageUrl ? 'MMS' : calculatedSegments >= 3 ? 'MMS' : 'SMS',
      dateTime: timestamp,
      isUrlShortningEnabled: shortenUrl,
      tags: mapSelectedTags(selectedTags),
      isTriggerCampaign: formType === formTypes.Replicate
        ? !!values.isTriggerCampaign
        : (type === CampaignModal.TriggerCampaign),
      events: (formType === formTypes.Replicate && values.isTriggerCampaign)
        || type === CampaignModal.TriggerCampaign
        ? getSelectedTriggerEvents()
        : [],
    },
    onCompleted() {
      history.push('/campaigns');
    },
    onError(data) {
      toast.error(getErrorMessage(data));
    },
  });

  const [testCampaign, { loading: testLoading }] = useMutation(TEST_CAMPAIGN, {
    variables: {
      name: values.name,
      text: values.text,
      unsubscribeMessage: values.unsubscribeMessage,
      status, // back-end asked to send status key
      ...(values.imageUrl && { imageUrl: values.imageUrl }),
      referralOptIn: includeReferralText,
      referralOptInText: includeReferralText ? values.referralOptInText : '',
      type: values.imageUrl ? 'MMS' : calculatedSegments >= 3 ? 'MMS' : 'SMS',
      dateTime: getConvertedTs(getDateByOffset(app.orgTimeZoneOffset)),
      customNumbers,
    },
    onCompleted(data) {
      if (data.campaign.success) {
        toast.success(data.campaign.message);
      } else {
        toast.error(data.campaign.message);
      }

      setCustomNumbers([]);
    },
    onError(data) {
      toast.error(getErrorMessage(data));
    },
  });

  const [getRecentCampaign, { loading: recentLoading }] = useLazyQuery(
    MOST_RECENT_CAMPAIGN,
    {
      fetchPolicy: 'network-only',
      onCompleted(data) {
        setSpamWarningModal(true);
        if (data.campaign != null) {
          setRecentCampaignData(data.campaign);
        } else {
          setOptimalCampaignModal(true);
        }
      },
      onError(data) {
        toast.error(getErrorMessage(data));
      },
    },
  );
  useEffect(() => {
    if (formType === formTypes.Edit) {
      const eventsData = mapTriggerEventOptions(values?.events);
      setDropdownOptions(eventsData);
    }
  }, [formType, values?.events]);
  const [updateCampaign, { loading: updating }] = useMutation(UPDATE_CAMPAIGN, {
    variables: {
      campaignId: values.id,
      name: values.name,
      text: values.text,
      unsubscribeMessage: values.unsubscribeMessage,
      status,
      ...(values.imageUrl && { imageUrl: values.imageUrl }),
      referralOptInText: includeReferralText ? values.referralOptInText : '',
      referralOptIn: includeReferralText,
      type: values.imageUrl ? 'MMS' : 'SMS',
      dateTime: timestamp,
      isUrlShortningEnabled: shortenUrl,
      tags: mapSelectedTags(selectedTags),
      isTriggerCampaign: formType === formTypes.Edit ? values?.isTriggerCampaign || (type === CampaignModal.TriggerCampaign) : (type === CampaignModal.TriggerCampaign),
      events: formType === formTypes.Edit ? mapSelectedTrigger(dropdownOptions) : (type === CampaignModal.TriggerCampaign ? mapSelectedTrigger([selectedTriggerDropdown]) : []),
    },
    onCompleted() {
      history.push('/campaigns');
    },
    onError(error) {
      toast.error(getErrorMessage(error));
    },
  });
  const [deleteCampaign, { loading: deleteCampaignLoading }] = useMutation(
    DELETE_CAMPAIGN,
    {
      variables: {
        campaignId: values.id,
      },
      onCompleted() {
        history.push('/campaigns');
      },
    },
  );

  useEffect(() => {
    if (sendNow) {
      setScheduleDate(getDateByOffset(app.orgTimeZoneOffset));
    }

    if (values.status === campaignStatus.Scheduled && !sendNow) {
      setSendUpdateButtonText('UPDATE');
    } else if (!sendNow) {
      setSendUpdateButtonText('SCHEDULE');
    } else if (type === CampaignModal.TriggerCampaign || (values?.isTriggerCampaign && (formType === formTypes.Edit || formType === formTypes.Replicate))) {
      setSendUpdateButtonText('SAVE');
    } else {
      setSendUpdateButtonText('SEND NOW');
    }
  }, [sendNow, values.status, app.orgTimeZoneOffset]);

  useEffect(() => {
    if (values.dateTime && formType === formTypes.Edit) {
      setScheduleDate(values.dateTime);
    }
  }, [values.dateTime, formType]);

  const history = useHistory();

  useEffect(() => {
    if (formType !== formTypes.Edit) {
      return;
    }
    const serverStatus = values.status;
    if (serverStatus === campaignStatus.Draft
      || serverStatus === campaignStatus.Scheduled
      || serverStatus === campaignStatus.Sent) {
      setStatus(serverStatus);
    }
  }, [formType, values.status]);

  useEffect(() => {
    // Edit only: keep local controls in sync with async-loaded campaign values.
    // Replicate seeds once via useState initializers after CreateCampaign gates mount.
    if (formType !== formTypes.Edit) {
      return;
    }
    setSendNow(values.sendMode === sendMode.SendNow);
    setincludeReferralText(values.referralOptIn);
    setShortenUrl(values.isUrlShortningEnabled);

    const formattedTags = values?.tags?.map((tag) => ({ label: tag, value: tag })) || [];
    setSelectedTags(formattedTags);

    const triggeredDropdown = mapTriggerEventOptions(values?.events);
    setSelectedTriggerDropdown(triggeredDropdown[0] || null);
  }, [
    formType,
    values.tags,
    values.sendMode,
    values.referralOptIn,
    values.isUrlShortningEnabled,
    values.events,
  ]);

  const UploadImage = useCallback(
    (files) => {
      uploadFiles({
        variables: {
          files,
        },
      });
    },
    [uploadFiles],
  );

  useEffect(() => {
    if (!sendNow) {
      setTimeStamp(getConvertedTs(scheduleDate));
    }
  }, [scheduleDate, sendNow, getConvertedTs]);

  const replaceUrlWithShortUrl = useCallback(
    (text) => {
      const regex = /(((https?:\/\/)|(www\.))[^\s]+)/g;
      const dummyShortUrl = `${shortUrlDomain}/${app?.shortcode ? `${app.shortcode}-rndStr` : 'rndStr'}`;
      const messageForCalc = text.replace(regex, dummyShortUrl);
      return messageForCalc;
    },
    [app],
  );

  useEffect(() => {
    // Canonical message composition (FR-1, FR-4): same delimiter and parts as API.
    const textForSegments = shortenUrl ? replaceUrlWithShortUrl(values.text || '') : (values.text || '');
    const fullMessageForSegments = composeCanonicalMessage(
      textForSegments,
      values.unsubscribeMessage,
      includeReferralText,
      values.referralOptInText,
    );
    const fullMessageEntered = composeCanonicalMessage(
      values.text || '',
      values.unsubscribeMessage,
      includeReferralText,
      values.referralOptInText,
    );

    const charCountForSegments = countMessageChars(fullMessageForSegments);
    const charCountEntered = countMessageChars(fullMessageEntered);
    const calculatedSegments = getPlivoSegmentCount(fullMessageForSegments);
    const unicodeFlag = !isGsm7(fullMessageForSegments);

    const willBeMMS = values.imageUrl || calculatedSegments >= 3;

    setCalculatedSegments(calculatedSegments);
    setSegments(willBeMMS ? 1 : calculatedSegments);
    setCredits(willBeMMS ? CREDITS_PER_MMS : calculatedSegments);
    setContainsUnicode(unicodeFlag);
    // Character count used for validation and display: segment-driving length (FR-6). When shortenUrl, also store entered length (FR-7).
    setCharacterCount(charCountForSegments);
    setEnteredCharacterCount(charCountEntered);
  }, [
    values.text,
    includeReferralText,
    values.imageUrl,
    values.referralOptInText,
    values.unsubscribeMessage,
    shortenUrl,
    replaceUrlWithShortUrl,
  ]);

  const onChangeHandler = (event) => {
    const { name, value } = event.target;
    setValues((values) => ({ ...values, [name]: value }));
  };

  useEffect(() => {
    if (uploading) {
      setFormError(FILE_UPLOADING_INDICATOR);
      return;
    }
    setFormError('');
  }, [uploading]);

  const onTimeChange = (value) => {
    if (value) {
      setScheduleDate(new Date(value));
    }
  };

  const onDateChange = (value) => {
    if (value) {
      setScheduleDate(value);
    }
  };
  const sendCampaign = () => {
    setButtonLoading(campaignStatus.Sent);
    finalSubmit(campaignStatus.Sent);
  };

  const finalSubmit = (intendedStatus) => {
    if (sendNow) {
      setTimeStamp(getConvertedTs(getDateByOffset(app.orgTimeZoneOffset)));
    }

    setTimeout(() => {
      // will wait for empty call stack as its moving forward without setting state
      setStatus(intendedStatus);
      if (
        formType === formTypes.Edit
        && (
          values.status === campaignStatus.Scheduled
          || values.status === campaignStatus.Draft
          || values?.isTriggerCampaign === true
        )
      ) {
        updateCampaign({ variables: { status: intendedStatus } });
        return;
      }
      createCampaign({ variables: { status: intendedStatus } });
    }, 0);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // FR-8: Plivo max 1600 (GSM-7) or 737 (Unicode); block when canonical message exceeds applicable limit.
    const maxLength = containsUnicode ? 737 : 1600;
    if (characterCount > maxLength) {
      return toast.error(
        `Characters limit exceeded. Message must be at most ${maxLength} characters for ${containsUnicode ? 'Unicode' : 'GSM-7'} messages.`,
      );
    }
    if (!selectedTriggerDropdown && type === CampaignModal.TriggerCampaign) {
      toast.error('Please select a Trigger Event');
      return;
    }
    const currentDate = getDateByOffset(app.orgTimeZoneOffset);
    switch (submitType) {
    case 'preview':
      setPreviewCampaignModal(true);
      break;

    case campaignStatus.Draft:
      setButtonLoading(campaignStatus.Draft);
      finalSubmit(campaignStatus.Draft);
      break;

    case campaignStatus.Sent:
      if (type === CampaignModal.TriggerCampaign || (formType === formTypes.Replicate && values?.isTriggerCampaign)) {
        sendCampaign();
      } else {
        setTimeout(() => {
          getRecentCampaign({
            variables: {
              dateTime: getConvertedTs(currentDate),
              campaignId: values?.id || null,
            },
          });
        }, 0);
      }
      break;

    case campaignStatus.Scheduled:
      if (scheduleDate.getTime() > currentDate.getTime()) {
        setTimeout(() => {
          getRecentCampaign({
            variables: {
              dateTime: timestamp,
              campaignId: values?.id || null,
            },
          });
        }, 0);
      } else {
        setFormError('Please select a future Date & Time');
      }

      break;

    default:
      break;
    }
  };

  const removeImage = () => {
    setImg(null);
    setCroppedImage(null);
    setImgName('');
    setValues((values) => ({ ...values, imageUrl: '' }));
    setFormError('');
  };

  const compress = new Compress();

  const onImageInpuChange = async (e) => {
    e.preventDefault();
    if (e.target.files?.length) {
      const files = [...e.target.files];
      setImgName(files[0].name);
      setCompressing(true);
      const file = await compressFiles(files);
      setCompressing(false);
      setImg(file);
    }
  };

  const compressFiles = async (files = []) => {
    const compressedFiles = await compress.compress(files, COMPRESS_OPTIONS);
    const img1 = compressedFiles[0];
    const base64str = img1.data;
    const imgExt = img1.ext;
    const file = Compress.convertBase64ToFile(base64str, imgExt);
    return file;
  };

  const setFileData = async (image) => {
    setFormError(FILE_UPLOADING_INDICATOR);
    setCompressing(true);
    const file = await compressFiles([image]);
    file.name = imgName || new Date();
    file.lastModifiedDate = new Date();
    setCompressing(false);
    setCroppedImage(file);
    setImg(file);
    UploadImage([file]);
  };
  const customStyles = {
    option: (provided) => ({
      ...provided,
      fontSize: '13px',
    }),
  };

  return (
    <>
      <BaseModal
        open={previewCampaignModal}
        modalType={ModalList.PreviewCampaignModal}
        onClose={setPreviewCampaignModal}
        customNumbers={customNumbers}
        setCustomNumbers={setCustomNumbers}
        sendTestCampaign={testCampaign}
        testResponse={testResponse}
        setTestResponse={setTestResponse}
        testLoading={testLoading}
      />
      <BaseModal
        open={selectTemplateVisible}
        modalType={ModalList.TemplateListModal}
        onClose={setSelectTemplateVisible}
        setValues={setValues}
      />
      <BaseModal
        open={showDeleteModal}
        modalType={ModalList.DeleteCampaignModal}
        onClose={setShowDeleteModal}
        onConfirm={() => deleteCampaign()}
        loading={deleteCampaignLoading}
      />
      <BaseModal
        open={spamWarningModal}
        modalType={ModalList.CampaignSendConfirm}
        onClose={setSpamWarningModal}
        setOptimalCampaignModal={setOptimalCampaignModal}
        recentCampaignData={recentCampaignData}
        isSendingNow={sendNow}
      />
      <BaseModal
        open={optimalCampaignModal}
        modalType={ModalList.OptimalTimingsReminder}
        onClose={setOptimalCampaignModal}
        loading={loading || updating}
        sendCampaign={sendCampaign}
        scheduleCampaign={() => finalSubmit(campaignStatus.Scheduled)}
        isSendingNow={sendNow}
      />

      <Form onSubmit={handleSubmit}>
        <Input
          top
          width="100%"
          type="text"
          header="Campaign Name *"
          onChange={(e) => onChangeHandler(e)}
          name="name"
          value={values.name || ''}
          required
        />

        <MessageDetails>
          <span
            style={{ display: 'flex', alignItems: 'center' }}
            data-tip="After choosing image you must have to click Crop"
          >
            Campaign Image (Optional)
            <Icon size={10} margin="0 5px" icon={IconEnum.infoRegular} />
          </span>
        </MessageDetails>

        {mmsEnabled === 'N' ? (
          <DisabledImagePlaceholder>
            MMS is not yet enabled for your MMS phone number. Please allow 48
            hours for this process to be completed.
          </DisabledImagePlaceholder>
        ) : (
          <ImageDataContainer>
            <ImageInputContainer
              opacity={values.imageUrl && files.length < 1 ? '0' : '1'}
            >
              {!img && !values.imageUrl && (
                <div
                  className="App"
                  style={{
                    background: '#f1f0ef',
                    padding: '80px',
                    position: 'relative',
                    textAlign: 'center',
                  }}
                >
                  <span style={{ color: '#828282' }}>
                    {compressing ? (
                      <b>Please wait compressing.....</b>
                    ) : (
                      'Drag and drop your image or click to Browse'
                    )}
                  </span>
                  <FileInput
                    type="file"
                    accept={ALLOWED_FILE_TYPES.join(',')}
                    onChange={onImageInpuChange}
                  />
                </div>
              )}

              {img && !croppedImage && (
                <ReCropper
                  img={img}
                  setFileData={setFileData}
                  reset={removeImage}
                />
              )}
            </ImageInputContainer>
          </ImageDataContainer>
        )}

        {(croppedImage || values.imageUrl) && (
          <div style={{ position: 'relative' }}>
            <RemoveIconDiv>
              <Icon
                icon={Icons.TimesCircleSolid}
                onClick={() => removeImage()}
                inactiveColor="red"
                size={15}
              />
            </RemoveIconDiv>
            <img
              alt="Cropped"
              style={{ height: '100%', width: '100%', maxHeight: '500px' }}
              src={
                values.imageUrl
                  ? values.imageUrl
                  : URL.createObjectURL(croppedImage)
              }
            />
          </div>
        )}

        <TextAreaInput
          width="100%"
          type="text"
          header="Text *"
          onChange={(e) => {
            onChangeHandler(e);
          }}
          name="text"
          value={values.text}
          required
        />
        {characterCount > (containsUnicode ? 737 : 1600) && (
          <div
            style={{ color: 'red', fontSize: '12px', margin: '10px 0 0 10px' }}
          >
            Characters limit exceeded. Message must be at most {containsUnicode ? 737 : 1600} characters for {containsUnicode ? 'Unicode' : 'GSM-7'} messages.
          </div>
        )}
        {/* MMS notice message */}
        {calculatedSegments >= 3 && !values.imageUrl && (
          <div style={{ color: 'blue', fontSize: '12px', margin: '10px 0 0 10px' }}>
            **Long campaign messages will be automatically upgraded to MMS to lower your credit usage. If you don't upload an image, your logo will be used.
          </div>
        )}
        <Button
          margin="20px 10px"
          text="Select Template"
          onClick={() => {
            setSelectTemplateVisible(true);
          }}
          type="button"
          width="max-content"
          height="20px"
        />

        <CheckBoxSection noBorder>
          <RadioContainer>
            <span
              style={{ display: 'flex', alignItems: 'center' }}
              data-tip="Referral marketing discounts, credit configurations, and uniquely identifiable referrer link will automatically be inserted at the cursors last position"
            >
              <Input
                width="25px"
                height="20px"
                type="checkbox"
                checked={includeReferralText}
                onChange={() => setincludeReferralText(!includeReferralText)}
              />

              <Label>Check here to include referral program opt-in text.</Label>
              <Icon size={10} margin="0 5px" icon={IconEnum.infoRegular} />
            </span>
          </RadioContainer>
          <RadioContainer marginXs="25px 0px" margin="0 0 0 20px">
            <span
              style={{ display: 'flex', alignItems: 'center' }}
              data-tip="URLs will be shortened to provide click-through tracking. Recipients will see URLs containing the branded shortcode."
            >
              <Input
                width="25px"
                height="20px"
                type="checkbox"
                margin={!isMobile ? '0 0 0 20px' : '0'}
                checked={shortenUrl}
                onChange={() => setShortenUrl(!shortenUrl)}
              />
              <Label>Track campaign conversions</Label>
              <Icon size={10} margin="0 5px" icon={IconEnum.infoRegular} />
            </span>
          </RadioContainer>
        </CheckBoxSection>

        <MessageDetails top>Unsubscribe Message </MessageDetails>
        <MessageDiv>
          <Text>{values.unsubscribeMessage}</Text>
        </MessageDiv>
        <TooltipStyles />
        <MessageDetails>Complete Message</MessageDetails>
        <MessageDiv>
          <Text>
            {values.text && (
              <>
                {values.text}
                <br />
              </>
            )}
            {includeReferralText && (
              <>
                {values.referralOptInText}
                <br />
              </>
            )}
            {values.unsubscribeMessage}
          </Text>
        </MessageDiv>
        <div data-help="campaign-form-primary-send" style={{ width: '100%' }}>
          <div style={{ marginTop: '10px' }}>
            <MessageDetails>
              Tags
              <span data-tip="Select one or more tags to target this campaign to specific contact. if no tags are selected, all opted-in contact will receive the campaign .">
                <Icon size={10} margin="0 5px" icon={IconEnum.infoRegular} />
              </span>
            </MessageDetails>{' '}
            <Select
              onChange={(e) => handleChange(e)}
              options={tagsOptions || []}
              isClearable={false}
              isMulti
              value={selectedTags}
              styles={customStyles}
            />
          </div>
          <MessageDetailsContainer>
            <MessageDetails>
            Message Type: {(values.imageUrl || calculatedSegments >= 3) ? 'MMS' : 'SMS'}
            </MessageDetails>
            {/* Calculating characters*/}
            <MessageDetails>
            Characters: {shortenUrl ? `${enteredCharacterCount} (${characterCount} shortened)` : characterCount}
            </MessageDetails>
            <MessageDetails>
            Segments: {segments} {containsUnicode && !(values.imageUrl || calculatedSegments >= 3) ? '(unicode/emoji)' : ''}
            </MessageDetails>
          </MessageDetailsContainer>
          <MessageDetailsContainer>
            <MessageDetails>
            Credits Per Message: {credits}
              <span data-tip="Credits were calculated based on shortened urls in your message">
                {shortenUrl && (
                  <Icon size={10} margin="0 5px" icon={IconEnum.infoRegular} />
                )}
              </span>
            </MessageDetails>
            {type !== CampaignModal.TriggerCampaign
              ? (
                <MessageDetails>
            Current Contact Count:{' '}
                  {!contactsCountLoading ? tagsContactsCount : 'Loading...'}
                </MessageDetails>
              )
              : ''}
            <MessageDetails>
            Total Estimated Credits:{' '}
              {!contactsCountLoading ? credits * tagsContactsCount : 'Loading...'}
              <span data-tip="SMS campaigns cost 1 credit per 160 characters per active contact.  For SMS messages that include 1 or more emojis, the total character limit is 70.  Messages over 160 characters or 70 characters, if they include emojis, will be split into multiple SMS messages.  Campaigns that include a photo automatically become an MMS message which cost 3 credits per active contact.  Note that MMS messages with rich media generally generate 4 times the conversions of SMS messages.">
                <Icon size={10} margin="0 5px" icon={IconEnum.infoRegular} />
              </span>
            </MessageDetails>
          </MessageDetailsContainer>
          {/* // Trigger Event For Trigger Campaign */}
          {type === CampaignModal.TriggerCampaign || (formType === formTypes.Replicate && values.isTriggerCampaign) ? (

            <div style={{ marginTop: '10px' }}>
              <MessageDetails>Trigger Event *</MessageDetails>
              <Select
                options={TriggerEvents}
                value={selectedTriggerDropdown}
                onChange={(e) => handleTriggerChange(e)}
                isMulti
              />
            </div>
          ) : ''}
          <InputSection
            justify="space-between"
            width={sendNow ? '80%' : '100%'}
            noBorder
          >
            {type === CampaignModal.GenCampagian || (formType === formTypes.Replicate && !values.isTriggerCampaign) ? (
              <>
                <RadioContainer>
                  <Input
                    width="25px"
                    height="30px"
                    checked={sendNow}
                    onChange={() => setSendNow(true)}
                    type="radio"
                    name="sendMode"
                    required
                  />
                  <Label>Send Now</Label>
                </RadioContainer>
                <RadioContainer ml={!isMobile && sendNow ? '0' : '9%'}>
                  <Input
                    width="25px"
                    height="30px"
                    checked={!sendNow}
                    onChange={() => setSendNow(false)}
                    type="radio"
                    name="sendMode"
                    required
                  />
                  <Label>Send Later </Label>
                </RadioContainer>
              </>
            ) : ''}
            <div style={{ display: 'flex' }}>
              {!sendNow && !isMobile && (
                <>
                  <DatePickerStyles />
                  <DateTimePicker
                    value={scheduleDate}
                    onChange={onDateChange}
                    minDate={getDateByOffset(app.orgTimeZoneOffset)}
                    format="MM-dd-y"
                    showLeadingZeros={false}
                    required
                    disableClock
                  />
                  <StyledTimePicker
                    style={{ width: 100 }}
                    showSecond={false}
                    placement="topRight"
                    onChange={(value) => onTimeChange(value)}
                    value={moment(scheduleDate)}
                    use12Hours
                    inputIcon={(
                      <Icon
                        size={17}
                        margin="0 5px"
                        onClick={() => { }}
                        icon={IconEnum.ClockRegular}
                      />
                    )}
                    allowEmpty={false}
                    format="hh:mm A"
                    required
                  />
                </>
              )}
            </div>
          </InputSection>
        </div>

        {!sendNow && isMobile && (
          <div
            style={{
              display: 'flex',
              width: '100%',
              justifyContent: 'center',
              margin: '15px 0px',
            }}
          >
            <DatePickerStyles />
            <DateTimePicker
              value={scheduleDate}
              onChange={onDateChange}
              minDate={getDateByOffset(app.orgTimeZoneOffset)}
              format="MM-dd-y"
              disableClock
              showLeadingZeros={true}
              required
            />
            <StyledTimePicker
              style={{ width: 100 }}
              showSecond={false}
              value={moment(scheduleDate)}
              onChange={(value) => onTimeChange(value)}
              format="hh:mm A"
              use12Hours
              placement="topRight"
              inputIcon={
                <Icon size={17} margin="0 5px" icon={IconEnum.ClockRegular} />
              }
              allowEmpty={false}
              required
            />
          </div>
        )}

        {formError && (
          <p style={{ color: 'red', fontSize: '13px' }}>{formError}</p>
        )}
        <>
          {formType === formTypes.Edit
            && !values?.isTriggerCampaign && values.executionStatus !== executionStatus.Pending ? (
              <h3
                style={{
                  width: '100%',
                  display: 'flex',
                  justifyContent: 'center',
                  color: 'red',
                }}
              >
              This campaign already sent and can't be modified
              </h3>
            ) : (
              <>
                <ButtonContainer noBorder>
                  {formType === formTypes.Edit && (
                    <Button
                      disabled={
                        formError === FILE_UPLOADING_INDICATOR
                      || updating
                      || loading
                      }
                      margin="10px 0px 0px 0px"
                      text="DELETE"
                      background="red"
                      type="button"
                      loading={deleteCampaignLoading}
                      minWidth="100px"
                      width="20%"
                      onClick={() => setShowDeleteModal(true)}
                    />
                  )}
                  {values.status !== campaignStatus.Scheduled && (
                    <Button
                      disabled={
                        formError === FILE_UPLOADING_INDICATOR
                      || updating
                      || loading
                      }
                      margin="10px 0px 0px 0px"
                      text="SAVE DRAFT"
                      type="submit"
                      minWidth="100px"
                      width="20%"
                      loading={buttonLoading === campaignStatus.Draft}
                      value={campaignStatus.Draft}
                      onClick={(e) => {
                        submitType = e.target.value;
                      }}
                    />
                  )}
                  <Button
                    disabled={
                      formError === FILE_UPLOADING_INDICATOR
                    || updating
                    || loading
                    }
                    type="submit"
                    margin="10px 0px 0px 0px"
                    text="PREVIEW"
                    value="preview"
                    minWidth="100px"
                    width="20%"
                    onClick={(e) => {
                      submitType = e.target.value;
                    }}
                  />
                  <Button
                    disabled={
                      formError === FILE_UPLOADING_INDICATOR
                    || updating
                    || loading
                    }
                    margin="10px 0px 0px 0px"
                    text={sendUpdateButtonText}
                    loading={recentLoading}
                    value={
                      sendNow ? campaignStatus.Sent : campaignStatus.Scheduled
                    }
                    onClick={(e) => {
                      submitType = e.target.value;
                    }}
                    minWidth="100px"
                    width="20%"
                  />
                </ButtonContainer>
              </>
            )}
        </>
      </Form>
      <ReactTooltip
        place="right"
        type="dark"
        effect="solid"
        clickable={false}
        multiline={true}
      />
    </>
  );
};

export default CampaignForm;
